import React, { useState, useEffect, useRef } from 'react';
import { Pagination, Message, Dropdown } from 'semantic-ui-react';
import 'semantic-ui-css/semantic.min.css';
import '../OutboundCampaign/OutboundCampaign.css';
import './OutboundSMSCampaignList.css';
import api from "../../../api";
import SmsCampaignStatusBadge from "./SmsCampaignStatusBadge";
import { SmsCampaignTypeBadge } from "./SmsCampaignTypeBadge";
import Modal from '../ChatSetup/components/Modal';
import Papa from "papaparse";
import { normalizePhoneNumber } from '../OutboundCampaign/scheduleUtils';
import constants from '../../../../src/util_funs/constant';
import ManualCustomerEntry from '../OutboundCampaign/customers/ManualCustomerEntry';
import CSVSampleDownloadButton from '../OutboundCampaign/CSVSampleDownloadButton';
import CustomerDetailsModalBody from '../OutboundCampaign/customers/CustomerDetailsModalBody';
import ConfirmationModal from '../Workspace/Botbuilder/Details/confusedbranch/DataConnect/ConfirmationModal';
import { DatatablePageSizeSelect } from '../CompletedChats/DatatablePageSizeSelect';
import { filterCampaignCustomers, useDebouncedValue } from '../OutboundCampaign/customers/filterCampaignCustomers';
import CheckboxOptionGroupSMS from './components/CheckboxOptionGroupSMS';

const phoneRegex = /^\(\d{3}\)\d{3}-\d{4}$/;
const nameRegex = /^(?!\d+$)(?!.*[.\-'\s]{2})[\p{L}0-9][\p{L}0-9.\-'\s]{0,48}[\p{L}0-9.]$/u;

const getCampaignSequences = (campaign) => {
  if (!campaign || typeof campaign !== "object") return [];
  return Array.isArray(campaign.sequences) ? campaign.sequences : [];
};

const isSequenceCampaignEnabled = (campaign) =>
  campaign?.is_sequence_campaign === true ||
  campaign?.is_sequence_campaign === 1;

const SMSCampaignDetails = ({
    campaign_id,
    user_id,
    onBackToList
}) => {
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [customersPerPage, setCustomersPerPage] = useState(10);
    const [customerSearch, setCustomerSearch] = useState('');
    const debouncedCustomerSearch = useDebouncedValue(customerSearch);
    const [webhookCopied, setWebhookCopied] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [showCSVModal, setShowCSVModal] = useState(false);
    const [showWebhookInfoModal, setShowWebhookInfoModal] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvError, setCsvError] = useState("");
    const [csvSuccess, setCsvSuccess] = useState("");
    const [csvProcessing, setCsvProcessing] = useState(false);
    const [manualCustomers, setManualCustomers] = useState({ name: '', phone_number: '', manual: true });
    const [errors, setErrors] = useState({});
    const [selectedContactIds, setSelectedContactIds] = useState([]);
    const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
    const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showVoiceSourceEditor, setShowVoiceSourceEditor] = useState(false);
    const [voiceSourceForm, setVoiceSourceForm] = useState({ inbound_enabled: false, outbound_enabled: false, outbound_voice_campaign_ids: [] });
    const [voiceCampaignOptions, setVoiceCampaignOptions] = useState([]);
    const [voiceCampaignsLoading, setVoiceCampaignsLoading] = useState(false);
    const [voiceSourceSaving, setVoiceSourceSaving] = useState(false);
    const [voiceSourceError, setVoiceSourceError] = useState('');
    const fileInputRef = useRef(null);
    const messageRef = useRef(null);
    const selectAllCheckboxRef = useRef(null);

    const isTriggered = campaign?.type === 'triggered';

    const headers = ['Name', 'Phone'];
    const sampleData = [
        ['John Doe', '(123)456-7890'],
        ['Jane Smith', '(098)765-4321'],
        ['Mike Johnson', '(112)233-4455'],
        ['Sarah Wilson', '(555)123-4567']
    ];

    const isCampaignCompleted = (status) => {
        return status === 'completed';
    };

    /** Same visibility as single-row delete (trash): only triggered campaigns. */
    const showBulkDelete = campaign?.type === 'triggered';
    /** Same enablement as clickable trash: triggered and campaign not completed. */
    const bulkDeleteEnabled =
        campaign?.type === 'triggered' && !isCampaignCompleted(campaign?.status);

    // Calculate pagination for customers
    const filteredCustomers = filterCampaignCustomers(customers, debouncedCustomerSearch);
    const totalCustomers = filteredCustomers.length;
    const totalPages = Math.max(1, Math.ceil(totalCustomers / customersPerPage) || 1);
    const activePage = Math.min(currentPage, totalPages);
    const startIndex = (activePage - 1) * customersPerPage;
    const endIndex = startIndex + customersPerPage;
    const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedCustomerSearch, customersPerPage]);

    useEffect(() => {
        setCurrentPage((p) => Math.min(p, totalPages));
    }, [totalPages]);

    const contactHasServerId = (c) => !!c?.id;
    const bulkSelectableContacts = currentCustomers.filter(contactHasServerId);
    const allBulkRowsSelectedOnPage =
        bulkSelectableContacts.length > 0 &&
        bulkSelectableContacts.every((c) => selectedContactIds.includes(c.id));

    useEffect(() => {
        const allowed = new Set(customers.filter((c) => c?.id).map((c) => c.id));
        setSelectedContactIds((prev) => prev.filter((id) => allowed.has(id)));
    }, [customers]);

    useEffect(() => {
        if (campaign?.type === 'triggered' && isCampaignCompleted(campaign?.status)) {
            setSelectedContactIds([]);
        }
    }, [campaign?.type, campaign?.status]);

    useEffect(() => {
        const el = selectAllCheckboxRef.current;
        if (!el || !showBulkDelete) return;
        const selectedOnPage = bulkSelectableContacts.filter((c) => selectedContactIds.includes(c.id));
        el.indeterminate =
            selectedOnPage.length > 0 && selectedOnPage.length < bulkSelectableContacts.length;
    }, [showBulkDelete, bulkSelectableContacts, selectedContactIds]);

    const toggleBulkCheckbox = (customer) => {
        if (!bulkDeleteEnabled || !contactHasServerId(customer)) return;
        setSelectedContactIds((prev) =>
            prev.includes(customer.id)
                ? prev.filter((id) => id !== customer.id)
                : [...prev, customer.id]
        );
    };

    const toggleSelectAllBulkCheckboxes = () => {
        if (!bulkDeleteEnabled) return;
        const ids = bulkSelectableContacts.map((c) => c.id);
        setSelectedContactIds((prev) => {
            const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
            if (allSelected) return prev.filter((id) => !ids.includes(id));
            return [...new Set([...prev, ...ids])];
        });
    };

    const customersTableColSpan = showBulkDelete ? 6 : 5;

    const normalizePhoneForCompare = (p) => String(p || '').replace(/\D/g, '').replace(/^1/, '') || '';

    const handleViewCustomer = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerDetailsModal(true);
    };

    const confirmBulkDelete = async () => {
        if (isCampaignCompleted(campaign?.status)) {
            setShowBulkConfirmModal(false);
            return;
        }

        const validSelectedIds = selectedContactIds.filter((id) =>
            customers.some((c) => c.id === id && contactHasServerId(c))
        );
        const contactIdsToDelete = [...new Set(validSelectedIds.map(Number))].filter(
            (id) => !Number.isNaN(id) && id > 0
        );
        if (contactIdsToDelete.length === 0) {
            setShowBulkConfirmModal(false);
            setMessage({
                type: 'error',
                text: 'No valid contacts selected.',
            });
            setTimeout(() => setMessage(null), 5000);
            return;
        }

        setLoading(true);

        const scrollToMessage = () => {
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
        };

        try {
            const res = await api.outbound.bulkDeleteSmsCampaignCustomers(campaign_id, contactIdsToDelete);
            const deletedCount =
                res?.data?.deleted_count ??
                res?.deleted_count ??
                (res?.status ? contactIdsToDelete.length : 0);

            await fetchCampaignDetails();
            setSelectedContactIds([]);

            if (res?.status) {
                setMessage({
                    type: 'success',
                    text: res.message || `${deletedCount} contact(s) removed from this campaign.`,
                });
            } else {
                setMessage({
                    type: 'error',
                    text:
                        res?.message ||
                        (deletedCount > 0
                            ? `${deletedCount} removed; some contacts could not be deleted.`
                            : 'Could not delete the selected contacts.'),
                });
            }
            scrollToMessage();
            setTimeout(() => setMessage(null), 7000);
        } catch (err) {
            console.error('Bulk delete error:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'An error occurred while deleting contacts.',
            });
            scrollToMessage();
            setTimeout(() => setMessage(null), 7000);
        } finally {
            setShowBulkConfirmModal(false);
            setLoading(false); 
        }
    };

    const handleCopyWebhook = async () => {
        try {
            if (campaign?.webhook_url) {
                await navigator.clipboard.writeText(campaign.webhook_url);
                setWebhookCopied(true);
                setTimeout(() => setWebhookCopied(false), 5000);
            }
        } catch (e) {
            console.error('Failed to copy webhook URL:', e);
        }
    };

    useEffect(() => {
        if (campaign_id) {
            fetchCampaignDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign_id]);

    const formatStatus = (status) => {
        if (!status) return '';
        if (status === 'approved' || status === 'twilio-approved') return 'Completed';
        if (status === 'non_marketing') return 'General Non-Marketing';
        return status
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getConsentTypeLabel = (value) =>
        constants.CONSENT_TYPE_OPTIONS.find(o => o.value === value)?.text
        || (value ? formatStatus(value) : "N/A");

    const fetchCampaignDetails = async () => {
        setLoading(true);
        setMessage(null);

        try {
            let u = new Date().toString().match(/([-\+][0-9]+)\s/)[1];
            let clientTimeZone = u.substring(0, 3) + ":" + u.substring(3, u.length);
            let time_zone = clientTimeZone;
            const response = await api.outbound.getSmsCampaign(campaign_id, time_zone);

            if (response.status) {
                setCampaign(response.data);
                setCustomers(response.data.customers || []);
            } else {
                setMessage({ type: 'error', text: response.message || 'Failed to fetch campaign details' });
            }
        } catch (error) {
            console.error('Error fetching campaign details:', error);
            setMessage({ type: 'error', text: 'An error occurred while fetching campaign details' });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (e, { activePage }) => {
        setCurrentPage(activePage);
    };

    const openVoiceSourceEditor = async () => {
        setVoiceSourceError('');
        const outboundVoiceCampaignIds = (campaign?.voice_sources?.outbound_voice_campaigns || []).map((c) => c.id);
        setVoiceSourceForm({
            inbound_enabled: !!campaign?.voice_sources?.inbound_enabled,
            outbound_enabled: outboundVoiceCampaignIds.length > 0,
            outbound_voice_campaign_ids: outboundVoiceCampaignIds,
        });
        setShowVoiceSourceEditor(true);

        if (!voiceCampaignOptions.length) {
            try {
                setVoiceCampaignsLoading(true);
                const res = await api.outbound.CampaignsList({ user_id, limit: 200 });
                const campaigns = res?.data?.campaigns;
                setVoiceCampaignOptions(
                    Array.isArray(campaigns) ? campaigns.map((c) => ({ key: c.id, value: c.id, text: c.name })) : []
                );
            } catch (e) {
                setVoiceCampaignOptions([]);
            } finally {
                setVoiceCampaignsLoading(false);
            }
        }
    };

    const saveVoiceSources = async () => {
        setVoiceSourceError('');

        if (voiceSourceForm.outbound_enabled && voiceSourceForm.outbound_voice_campaign_ids.length === 0) {
            setVoiceSourceError('Select at least one outbound voice campaign, or uncheck this source.');
            return;
        }

        setVoiceSourceSaving(true);
        try {
            const res = await api.outbound.saveSmsCampaignVoiceSources(campaign_id, voiceSourceForm);
            if (res?.status) {
                setShowVoiceSourceEditor(false);
                await fetchCampaignDetails();
            } else {
                setVoiceSourceError(res?.message || 'Failed to save voice sources');
            }
        } catch (e) {
            setVoiceSourceError('An error occurred while saving voice sources');
        } finally {
            setVoiceSourceSaving(false);
        }
    };

    const displayScheduleType = (schedule_type) => {
        switch (schedule_type) {
            case 'one_time':
                return 'One Time';
            case 'ongoing':
                return 'Ongoing';
            default:
                return schedule_type || 'N/A';
        }
    };

    const getSendTypeDisplay = (sendType) => {
        switch (sendType) {
            case 'now':
                return 'Send Now';
            case 'scheduled':
                return 'Scheduled';
            default:
                if (!sendType) return 'N/A';
                return sendType
                    .replace(/[-_]/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase());
        }
    };

    const dayScheduleBadgeLabel = (day) => {
        const abbrevs = {
            Monday: 'MON',
            Tuesday: 'TUE',
            Wednesday: 'WED',
            Thursday: 'THU',
            Friday: 'FRI',
            Saturday: 'SAT',
            Sunday: 'SUN',
        };
        return abbrevs[day] || String(day).slice(0, 3).toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        } catch (error) {
            return 'N/A';
        }
    };

    /** Schedule card: e.g. May 13, 2026 */
    const formatScheduleDisplayDate = (value) => {
        if (value == null || value === '') return '—';
        const raw = String(value).trim();
        const tryDate = new Date(raw);
        if (!isNaN(tryDate.getTime())) {
            return tryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
        }
        return raw;
    };

    /** Schedule card: 24h HH:mm → 12h with AM/PM; passthrough if already contains AM/PM */
    const formatScheduleTime12h = (value) => {
        if (value == null || value === '') return '—';
        const s = String(value).trim();
        if (/am|pm/i.test(s)) return s;
        const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
        if (!m) return s;
        let h = parseInt(m[1], 10);
        const min = m[2];
        const ap = h >= 12 ? 'PM' : 'AM';
        const h24 = h;
        if (h24 === 0) {
            h = 12;
        } else if (h24 > 12) {
            h = h24 - 12;
        }
        return `${h}:${min} ${ap}`;
    };

    const phoneFormat = (p) => {
        let d = String(p ?? '').replace(/\D/g, '');
        if (d.length === 11 && d[0] === '1') d = d.slice(1);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `(${d.slice(0, 3)})${d.slice(3)}`;
        return `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6, 10)}`;
    };

    const executionStatusBadgeClass = (status) => {
        const ex = String(status || '').toLowerCase();
        if (ex === 'sent') return 'badge badge-primary';
        if (ex === 'failed') return 'badge badge-danger';
        if (ex === 'delivered') return 'badge badge-success';
        return 'badge badge-secondary';
    };

    function validateName(name) {
        const errors = [];
        if (!name || name.trim() === "") {
            errors.push("Name is required.");
        } else if (name.length < 2) {
            errors.push("The name field must be at least 2 characters.");
        } else if (name.length > 50) {
            errors.push("Name cannot exceed 50 characters.");
        } else if (!nameRegex.test(name.trim())) {
            errors.push("Name can only contain letters, digits, spaces, hyphens, and apostrophes.");
        }
        return errors;
    }

    function validateCSVHeaders(headers, requiredHeaders) {
        const errors = [];
        if (!headers || headers.length === 0) {
            errors.push("CSV has no headers.");
            return errors;
        }
        const cleanHeaders = headers.map(h => h.trim().toLowerCase());

        const missing = requiredHeaders.filter(h => !cleanHeaders.includes(h.toLowerCase()));
        if (missing.length > 0) {
            errors.push(`CSV is missing required column(s): ${missing.join(", ")}.`);
            return errors;
        }
        return errors;
    }

    const validateCustomer = () => {
        const newErrors = {};
        const name = manualCustomers.name ? manualCustomers.name.trim() : "";
        const phone = manualCustomers.phone_number;
        const nameErrors = validateName(name);
        if (nameErrors.length > 0) {
            newErrors.name = nameErrors[0];
        }
        if (!phone) {
            newErrors.phone_number = "Phone number is required.";
        } else if (!phoneRegex.test(phone)) {
            newErrors.phone_number = "Valid phone number required ((000)000-0000)";
        }
        return newErrors;
    };

    const handleChange = (field, value) => {
        setErrors({});
        if (field === "phone_number") value = phoneFormat(value);
        setManualCustomers(prev => ({ ...prev, [field]: value }));
    };

    const handleAddCustomer = async () => {
        const validationErrors = validateCustomer();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        try {
            const encryptedId = btoa(`${user_id}/${campaign_id}`);
            const response = await api.outbound.storeSmsCampaignCustomers(
                encryptedId,
                [{
                    name: manualCustomers.name.trim(),
                    phone_number: manualCustomers.phone_number,
                    manual: true,
                }],
                { skipWebhookQueueDelay: true }
            );

            if (response?.status) {
                if (response.inserted_count > 0) {
                    setManualCustomers({ name: '', phone_number: '', manual: true });
                    await fetchCampaignDetails();
                    setMessage({ type: 'success', text: 'Customer added successfully!' });

                    setTimeout(() => {
                        if (messageRef.current) {
                            messageRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest'
                            });
                        }
                    }, 150);

                    setTimeout(() => setMessage(null), 5000);
                } else {
                    setMessage({
                        type: 'error',
                        text: 'Customer could not be added.',
                    });
                    setTimeout(() => setMessage(null), 5000);
                }
            } else {
                const transformedErrors = {};
                if (response?.errors) {
                    Object.keys(response.errors).forEach((key) => {
                        const match = key.match(/^customers\.0\.(.+)$/);
                        if (match) {
                            const field = match[1];
                            let errorMessage = Array.isArray(response.errors[key])
                                ? response.errors[key][0]
                                : response.errors[key];
                            errorMessage = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
                            errorMessage = errorMessage.replace(/customers\.0\./g, '');
                            transformedErrors[field] = errorMessage;
                        }
                    });
                }

                if (Object.keys(transformedErrors).length === 0) {
                    if (response?.message && response.message !== 'Validation errors') {
                        transformedErrors.phone_number = response.message;
                    } else {
                        transformedErrors.phone_number = 'Failed to add customer';
                    }
                }

                setErrors(transformedErrors);
                setTimeout(() => {
                    setErrors({});
                }, 5000);
            }
        } catch (error) {
            setErrors({ phone_number: error.message || 'Network error occurred' });
            setTimeout(() => {
                setErrors({});
            }, 5000);
        } finally {
            setLoading(false);
        }
    };

    const clearCSVInputAndErrors = () => {
        setCsvFile(null);
        setCsvError("");
        setCsvSuccess("");
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };
    const getTypeBadge = (type) => {
        if (!type) return null;

        const normalizedType = type.toLowerCase();
        const capitalizedText = normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);

        let icon = null;
        if (normalizedType === 'voice') {
            icon = <i className="la la-phone campaign-info-card__type-pill-icon" aria-hidden="true" />;
        } else if (normalizedType === 'sms') {
            icon = <i className="la la-commenting campaign-info-card__type-pill-icon" aria-hidden="true" />;
        } else if (normalizedType === 'triggered') {
            icon = <i className="la la-bolt campaign-info-card__type-pill-icon" aria-hidden="true" />;
        }

        return (
            <span className="campaign-info-card__type-pill">
                {icon}
                {capitalizedText}
            </span>
        );
    };

    /** Shared sizing for rule-section Update actions (reference layout: full-width primary in actions column) */
    const CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE = {
        boxSizing: 'border-box',
        minHeight: 40,
        width: '100%',
        borderRadius: 6,
        padding: '10px 16px',
        fontSize: '14px',
        lineHeight: 1.25,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '600',
        border: 'none',
        background: '#0066FF',
        color: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
    };

    /** Mapping Guide on Webhook URL card — same look as rule CTAs, not full-width */
    const WEBHOOK_MAPPING_GUIDE_BTN_STYLE = {
        ...CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE,
        width: 'auto',
        minWidth: 0,
        flexShrink: 0,
    };

    const handleCSVUpload = () => {
        if (!csvFile) {
            setCsvError(["Please select a CSV file."]);
            return;
        }

        const isCSV = csvFile.type === "text/csv" || csvFile.name.endsWith(".csv");
        if (!isCSV) {
            setCsvError(["Please upload a valid CSV file (.csv)"]);
            return;
        }

        setCsvProcessing(true);
        setCsvError("");
        setCsvSuccess("");

        Papa.parse(csvFile, {
            header: true,
            skipEmptyLines: true,
            transformHeader: function (header) {
                return header.trim();
            },
            complete: async function (results) {
                try {
                    const headers = results.meta.fields || [];
                    const headerErrors = validateCSVHeaders(headers, ["name", "phone"]);
                    if (headerErrors.length > 0) {
                        setCsvError(headerErrors);
                        setCsvProcessing(false);
                        setTimeout(() => setCsvError(""), 5000);
                        return;
                    }
                    const parsed = results.data;
                    if (!parsed || parsed.length === 0) {
                        setCsvError(["CSV file is empty. Please add at least one customer."]);
                        setCsvProcessing(false);
                        setTimeout(() => setCsvError(""), 5000);
                        return;
                    }
                    const validEntries = [];
                    const invalidEntries = [];

                    const getRowValue = (row, key) => {
                        const k = Object.keys(row || {}).find((c) => String(c).trim().toLowerCase() === key);
                        return k != null ? row[k] : undefined;
                    };

                    parsed.forEach((row, index) => {
                        const name = getRowValue(row, 'name')?.trim();
                        const phone_number = normalizePhoneNumber(getRowValue(row, 'phone'));
                        const nameErrors = validateName(name);
                        const errors = [...nameErrors];
                        if (!phone_number) {
                            errors.push('Phone number is missing or blank.');
                        } else if (phone_number.length !== 10) {
                            errors.push(`Phone number must be 10 digits.`);
                        }

                        if (errors.length > 0) {
                            invalidEntries.push({
                                row: index + 2,
                                name: name || "Blank",
                                phone_number: getRowValue(row, 'phone') || "Blank",
                                errors,
                            });
                        } else {
                            const dynamic_variables = {};
                            Object.keys(row || {}).forEach((key) => {
                                if (!key) return;
                                const trimmedKey = String(key).trim();
                                if (!trimmedKey) return;
                                const keyNoSpaces = trimmedKey.replace(/\s+/g, '');
                                if (!keyNoSpaces) return;
                                if (keyNoSpaces.toLowerCase() === "name" || keyNoSpaces.toLowerCase() === "phone") return;
                                const value = row[key];
                                if (value === undefined || value === null) return;
                                const str = String(value).trim();
                                if (str === "") return;
                                dynamic_variables[keyNoSpaces] = str;
                            });

                            validEntries.push({
                                name,
                                phone_number: getRowValue(row, 'phone'),
                                manual: false,
                                ...(Object.keys(dynamic_variables).length > 0 ? { dynamic_variables } : {})
                            });
                        }
                    });

                    if (invalidEntries.length > 0) {
                        const allErrors = [];
                        invalidEntries.forEach(invalid => {
                            allErrors.push(`Row ${invalid.row}: ${invalid.errors.join(', ')}`);
                        });
                        setCsvError(allErrors);
                        setCsvProcessing(false);
                        setTimeout(() => setCsvError(""), 5000);
                        return;
                    }

                    if (validEntries.length > 0) {
                        try {
                            const customersToAdd = validEntries.map(entry => ({
                                name: entry.name.trim(),
                                phone_number: entry.phone_number,
                                ...(entry.dynamic_variables ? { dynamic_variables: entry.dynamic_variables } : {})
                            }));

                            const encryptedId = btoa(`${user_id}/${campaign_id}`);
                            const response = await api.outbound.storeSmsCampaignCustomers(
                                encryptedId,
                                customersToAdd,
                                { skipWebhookQueueDelay: true }
                            );

                            if (response?.status) {
                                if (response.inserted_count > 0) {
                                    setCsvSuccess(`${response.inserted_count} customer(s) added successfully.`);
                                    setMessage({ type: "success", text: `${response.inserted_count} customer(s) added successfully!` });
                                    await fetchCampaignDetails();
                                } else {
                                    setCsvError(["No customers were added."]);
                                }

                                setTimeout(() => {
                                    if (messageRef.current) {
                                        messageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }
                                }, 150);
                                setTimeout(() => {
                                    setShowCSVModal(false);
                                    clearCSVInputAndErrors();
                                }, 2000);
                            } else {
                                setCsvError(["Failed to add customers from CSV."]);
                                setTimeout(() => setCsvError(""), 5000);
                            }
                        } catch (error) {
                            setCsvError([error.message || "Network error occurred"]);
                            setTimeout(() => setCsvError(""), 5000);
                        }
                    } else {
                        setCsvError(["No valid customers found in CSV file."]);
                        setTimeout(() => setCsvError(""), 5000);
                    }

                    setCsvProcessing(false);
                } catch (error) {
                    console.error("Error processing CSV:", error);
                    setCsvProcessing(false);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="ui loading form"></div>
        );
    }

    return (
        <div className="campaign-details-page" style={{ maxWidth: '100%', minWidth: 0 }}>
            {/* Message Component */}
            {message && (
                <div ref={messageRef}>
                    <Message
                        className="campaign-page-message"
                        color={
                            message.type === 'success'
                                ? 'green'
                                : message.type === 'info'
                                  ? 'blue'
                                  : 'red'
                        }
                    >
                        {message?.text}
                    </Message>
                </div>
            )}

            {/* Header */}
            <div className="d-flex flex-wrap justify-content-start align-items-center mb-4 gap-2">
                <div>
                    <button
                        type="button"
                        className="btn btn-sm linear-gradient yr-submitbtn campaign-rules-ref-card__mapping-guide-btn"
                        onClick={onBackToList}
                    >
                        <i className="la la-arrow-left" style={{ marginTop: "-6px" }} aria-hidden="true" />
                        Back To List
                    </button>
                </div>
            </div>

            {/* Campaign Info & Customers — align-items-start so right column height does not track the left */}
            <div className="row align-items-start">
                <div className="col-12 col-lg-6 mb-4">
                    <div className="card campaign-info-card campaign-info-card--ref mb-3 w-100">
                        <div className="card-body campaign-info-card__body">
                            <div className="campaign-info-card__header">
                                <i className="la la-file-text-o la-2x" aria-hidden="true" />
                                <h2 className="campaign-info-card__heading">Campaign Info</h2>
                            </div>

                            <div className="campaign-info-card__main">
                                <div className="campaign-info-card__column">
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Campaign name</span>
                                        <div className="campaign-info-card__value text-break">
                                            {campaign.name || '—'}
                                        </div>
                                    </div>

                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Send type</span>
                                        <div className="campaign-info-card__value">{getSendTypeDisplay(campaign.send_type)}</div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Schedule Type</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.schedule
                                                ? displayScheduleType(campaign.schedule.schedule_type)
                                                : '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Workspace name</span>
                                        <div className="campaign-info-card__value text-break">
                                            {campaign.workspace_name ? campaign.workspace_name : '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">Timezone</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.schedule?.timezone || '—'}
                                        </div>
                                    </div>
                                    {(isSequenceCampaignEnabled(campaign)) && (
                                        <div className="campaign-info-card__field">
                                            <span className="campaign-info-card__label">Sequence campaign</span>
                                            <div className="campaign-info-card__value">Yes</div>
                                        </div>
                                    )}
                                </div>

                                <div className="campaign-info-card__column">
                                    <div className="campaign-info-card__field" style={{ marginTop : '-5px'}}>
                                        <span className="campaign-info-card__label">Campaign type</span>
                                        <div className="campaign-info-card__value">{getTypeBadge(campaign.type)}</div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Status</span>
                                        <div className="campaign-info-card__value">
                                            <SmsCampaignStatusBadge status={campaign.status} />
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Campaign category</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.campaign_category ? formatStatus(campaign.campaign_category) : '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Consent type</span>
                                        <div className="campaign-info-card__value">
                                            {getConsentTypeLabel(campaign.consent_type) || '—'}
                                        </div>
                                    </div>

                                    <div className="campaign-info-card__field campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">SMS Number</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.outbound_number?.phone || campaign.outbound_twilio_number?.phone || '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {campaign.consent_type === 'other' && campaign.consent_notes && (
                                <div className="campaign-info-card__consent-notes">
                                    <div className="campaign-info-card__field campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">Consent notes</span>
                                        <div className="campaign-info-card__value text-break">{campaign.consent_notes}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Schedule Info Card */}
                    {campaign.schedule && (
                        <div className="mt-2">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="card shadow-sm w-100 campaign-details-schedule-card">
                                        <div className="card-body p-4">
                                            <div className="d-flex align-items-center mb-4">
                                                <i className="la la-calendar la-2x mr-3" aria-hidden="true" />
                                                <h2 className="mb-0 campaign-info-card__heading">Schedule Info</h2>
                                            </div>
                                            <div className="campaign-details-schedule-card__grid">
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Start date</div>
                                                    <div className="font-weight-bold text-dark text-break">
                                                        {formatScheduleDisplayDate(campaign.schedule.start_date)}
                                                    </div>
                                                </div>
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Start time</div>
                                                    <div className="font-weight-bold text-dark">
                                                        {formatScheduleTime12h(campaign.schedule.start_time)}
                                                    </div>
                                                </div> 
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Stop time</div>
                                                    <div className="font-weight-bold text-dark">
                                                        {formatScheduleTime12h(campaign.schedule.end_time)}
                                                    </div>
                                                </div>
                                                <div className="campaign-details-schedule-card__days">
                                                    <div className="campaign-details-schedule-card__days-row">
                                                        <span className="small text-muted campaign-details-schedule-card__days-label">
                                                            Days
                                                        </span>
                                                        <div className="campaign-details-schedule-card__days-badges">
                                                            {campaign.schedule.days && campaign.schedule.days.length > 0 ? (
                                                                campaign.schedule.days.map((day, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="badge badge-primary text-white font-weight-bold campaign-details-schedule-card__day-badge d-inline-flex align-items-center justify-content-center"
                                                                        title={day}
                                                                    >
                                                                        {dayScheduleBadgeLabel(day)}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Webhook URL - Triggered only */}
                                {campaign.webhook_url && (
                                    <div className="col-12 mb-3">
                                        <div className="card shadow-sm w-100">
                                            <div className="card-body py-4">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center min-w-0 pr-3 mb-3 mb-md-0 flex-grow-1" style={{ paddingLeft: "3px" }}>
                                                        <div className="campaign-rules-ref-card__icon" aria-hidden="true" style={{ marginTop: "-23px" }}>
                                                            <i className="la la-link la-2x" />
                                                        </div>
                                                        <div className="min-w-0 flex-grow-1" style={{ marginLeft: 25 }}>
                                                            <h2 className="mb-1 campaign-rules-ref-card__title">
                                                                Webhook URL
                                                            </h2>
                                                            <div className="campaign-rules-ref-card__webhook-url-row mb-0">
                                                                <div className="campaign-rules-ref-card__webhook-url-field">
                                                                    <span className="text-muted small text-break campaign-rules-ref-card__webhook-url">
                                                                        {campaign.webhook_url}
                                                                    </span>
                                                                </div>
                                                                <div className="campaign-rules-ref-card__webhook-url-actions">
                                                                    <button
                                                                        type="button"
                                                                        className={`campaign-rules-ref-card__webhook-copy${
                                                                            webhookCopied
                                                                                ? ' campaign-rules-ref-card__webhook-copy--done'
                                                                                : ''
                                                                        }`}
                                                                        onClick={handleCopyWebhook}
                                                                        title={webhookCopied ? 'Copied' : 'Copy URL'}
                                                                        aria-label="Copy webhook URL"
                                                                    >
                                                                        <i
                                                                            className={
                                                                                webhookCopied
                                                                                    ? 'la la-check'
                                                                                    : 'la la-copy'
                                                                            }
                                                                            aria-hidden="true"
                                                                        />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm linear-gradient yr-submitbtn campaign-rules-ref-card__mapping-guide-btn"
                                                                        onClick={() => setShowWebhookInfoModal(true)}
                                                                        title="Webhook Mapping Guide"
                                                                        style={WEBHOOK_MAPPING_GUIDE_BTN_STYLE}
                                                                    >
                                                                        <span>Mapping Guide</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Manage Customers Section for Triggered Campaigns */}
                                {isTriggered && (
                                    <div className="col-12 mb-3">
                                        <div className="card shadow-sm w-100">
                                            <div className="card-body py-4">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center min-w-0 pr-3 mb-3 mb-md-0" style={{ paddingLeft: "3px" }}>
                                                        <div className="campaign-rules-ref-card__icon" aria-hidden="true" style={{ marginTop: "-10px" }}>
                                                            <i className="la la-users la-2x" />
                                                        </div>
                                                        <div className="min-w-0" style={{ marginLeft: 25 }}>
                                                            <h2 className="mb-1 campaign-rules-ref-card__title">Manage Customers</h2>
                                                            <div className="d-flex flex-wrap align-items-center justify-content-md-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm mr-2 mb-2 mb-md-0"
                                                                    style={{
                                                                        fontSize: 14,
                                                                        fontWeight: 600,
                                                                        opacity: isCampaignCompleted(campaign?.status) ? 0.5 : 1,
                                                                        cursor: isCampaignCompleted(campaign?.status)
                                                                            ? 'not-allowed'
                                                                            : 'pointer',
                                                                    }}
                                                                    onClick={() => {
                                                                        if (!isCampaignCompleted(campaign?.status)) {
                                                                            setShowManualEntry(true);
                                                                        }
                                                                    }}
                                                                    disabled={isCampaignCompleted(campaign?.status)}
                                                                >
                                                                    Add Customer Manually
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm mr-2 mb-2 mb-md-0"
                                                                    style={{
                                                                        fontSize: 14,
                                                                        fontWeight: 600,
                                                                        opacity: isCampaignCompleted(campaign?.status) ? 0.5 : 1,
                                                                        cursor: isCampaignCompleted(campaign?.status)
                                                                            ? 'not-allowed'
                                                                            : 'pointer',
                                                                    }}
                                                                    onClick={() => {
                                                                        if (!isCampaignCompleted(campaign?.status)) {
                                                                            setShowCSVModal(true);
                                                                        }
                                                                    }}
                                                                    disabled={isCampaignCompleted(campaign?.status)}
                                                                >
                                                                    Upload CSV
                                                                </button>
                                                                <CSVSampleDownloadButton
                                                                    filename="campaign_customers_sample.csv"
                                                                    headers={headers}
                                                                    sampleData={sampleData}
                                                                    className="btn btn-outline-primary btn-sm mb-2 mb-md-0"
                                                                    fontWeight={600}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {showManualEntry && (
                                                    <div className="customer-manager-container mt-3">
                                                        <ManualCustomerEntry
                                                            show={showManualEntry}
                                                            manualCustomers={manualCustomers}
                                                            errors={errors}
                                                            handleChange={handleChange}
                                                            handleAddCustomer={handleAddCustomer}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Voice Contact Sources Section for Triggered Campaigns */}
                                {isTriggered && (
                                    <div className="col-12 mb-3">
                                        <div className="card shadow-sm w-100">
                                            <div className="card-body py-4">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center min-w-0 pr-3 mb-3 mb-md-0" style={{ paddingLeft: "3px" }}>
                                                        <div className="campaign-rules-ref-card__icon" aria-hidden="true" style={{ marginTop: "-10px" }}>
                                                            <i className="la la-phone la-2x" />
                                                        </div>
                                                        <div className="min-w-0" style={{ marginLeft: 25 }}>
                                                            <h2 className="mb-1 campaign-rules-ref-card__title">Call Data Source</h2>
                                                            {campaign?.voice_sources?.inbound_enabled || (campaign?.voice_sources?.outbound_voice_campaigns || []).length > 0 ? (
                                                                <div style={{ fontSize: 14 }}>
                                                                    {campaign?.voice_sources?.inbound_enabled && (
                                                                        <div>Inbound Calls</div>
                                                                    )}
                                                                    {(campaign?.voice_sources?.outbound_voice_campaigns || []).length > 0 && (
                                                                        <div>
                                                                            Outbound: {campaign.voice_sources.outbound_voice_campaigns.map((c) => c.name).join(', ')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div style={{ fontSize: 14, color: '#888' }}>No voice sources configured.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        style={{ fontSize: 14, fontWeight: 600 }}
                                                        onClick={openVoiceSourceEditor}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>

                                                {showVoiceSourceEditor && (
                                                    <div className="customer-manager-container mt-3">
                                                        <CheckboxOptionGroupSMS
                                                            name="voice_data_source_edit"
                                                            selectedValues={[
                                                                ...(voiceSourceForm.inbound_enabled ? ["inbound"] : []),
                                                                ...(voiceSourceForm.outbound_enabled || voiceSourceForm.outbound_voice_campaign_ids.length > 0 ? ["outbound"] : []),
                                                            ]}
                                                            onChange={(values) => {
                                                                const outboundChecked = values.includes("outbound");
                                                                setVoiceSourceForm((prev) => ({
                                                                    ...prev,
                                                                    inbound_enabled: values.includes("inbound"),
                                                                    outbound_enabled: outboundChecked,
                                                                    outbound_voice_campaign_ids: outboundChecked ? prev.outbound_voice_campaign_ids : [],
                                                                }));
                                                            }}
                                                            options={[
                                                                {
                                                                    value: "inbound",
                                                                    label: "Inbound Calls",
                                                                    description: "Automatically manage missed and abandoned incoming calls.",
                                                                },
                                                                {
                                                                    value: "outbound",
                                                                    label: "Outbound Calls",
                                                                    description: "Automatically manage missed and abandoned calls from your outbound voice campaigns.",
                                                                },
                                                            ]}
                                                        />

                                                        {(voiceSourceForm.outbound_enabled || voiceSourceForm.outbound_voice_campaign_ids.length > 0) && (
                                                            <div className="form-group mt-1">
                                                                <label className="pd10botbuilder" style={{ paddingTop: 0 }}>Outbound Voice Campaign(s)</label>
                                                                <Dropdown
                                                                    placeholder="Select outbound voice campaign(s)"
                                                                    fluid
                                                                    selection
                                                                    multiple
                                                                    search
                                                                    loading={voiceCampaignsLoading}
                                                                    options={voiceCampaignOptions}
                                                                    value={voiceSourceForm.outbound_voice_campaign_ids}
                                                                    onChange={(e, { value }) =>
                                                                        setVoiceSourceForm((prev) => ({ ...prev, outbound_voice_campaign_ids: value }))
                                                                    }
                                                                    style={{ border: "1px solid #e1e5e9", borderRadius: "5px", padding: "10px 15px", fontSize: "14px" }}
                                                                />
                                                            </div>
                                                        )}
                                                        {voiceSourceError && (
                                                            <div className="text-danger mt-2" style={{ fontSize: 14 }}>{voiceSourceError}</div>
                                                        )}
                                                        <div className="mt-3">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm mr-2"
                                                                disabled={voiceSourceSaving}
                                                                onClick={saveVoiceSources}
                                                            >
                                                                {voiceSourceSaving ? 'Saving...' : 'Save'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm"
                                                                disabled={voiceSourceSaving}
                                                                onClick={() => setShowVoiceSourceEditor(false)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sequence Campaign details — always outside schedule so Send Now campaigns show it */}
                    {isSequenceCampaignEnabled(campaign) && (
                        <div className="mt-2 mb-3">
                            <div className="card shadow-sm w-100">
                                <div className="card-body py-4">
                                    <div className="d-flex align-items-center mb-3" style={{ paddingLeft: "3px" }}>
                                        <div className="campaign-rules-ref-card__icon" aria-hidden="true">
                                            <i className="la la-list-ol la-2x" />
                                        </div>
                                        <div style={{ marginLeft: 25 }}>
                                            <h2 className="mb-1 campaign-rules-ref-card__title">
                                                Sequence Campaign
                                            </h2>
                                            <p className="mb-0 campaign-rules-ref-card__desc">
                                                Follow-up texts sent automatically to the same contacts after the defined intervals.
                                            </p>
                                        </div>
                                    </div>

                                    {getCampaignSequences(campaign).length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-bordered mb-0" style={{ background: "#fff" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "15%" }}>Sequence</th>
                                                        <th>Workspace</th>
                                                        <th style={{ width: "25%" }}>Days until send</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getCampaignSequences(campaign).map((step, index) => {
                                                        const days =
                                                            step.days_until_send ??
                                                            step.days ??
                                                            step.in_days ??
                                                            null;
                                                        return (
                                                            <tr key={step.id || step.sequence_id || `sequence-${index}`}>
                                                                <td>
                                                                    #{""}
                                                                    {step.sequence_number || index + 1}
                                                                </td>
                                                                <td className="text-break">
                                                                    {step.workspace_name ||
                                                                        step.workspace?.name ||
                                                                        (step.workspace_id
                                                                            ? `Workspace #${step.workspace_id}`
                                                                            : "—")}
                                                                </td>
                                                                <td>
                                                                    {days ?? "—"}
                                                                    {days
                                                                        ? Number(days) === 1
                                                                            ? " day"
                                                                            : " days"
                                                                        : ""}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                                            No sequence steps are available for this campaign yet.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Panel - Customers Info Table */}
                <div className="col-12 col-lg-6 mb-4">
                    <div className="card shadow-sm w-100 d-flex flex-column">
                        <div className="card-body d-flex flex-column flex-grow-1">
                            <div className="d-flex align-items-center mb-3">
                                <i className="la la-users la-2x mr-3" aria-hidden="true" />
                                <h2 className="mb-0 campaign-info-card__heading">Customers Info</h2>
                            </div>
                            <div className="customers-info-toolbar">
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <div className="kt-input-icon kt-input-icon--right header-search">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by name, phone, or date..."
                                            value={customerSearch}
                                            onChange={(e) => {
                                                setCustomerSearch(e.target.value);
                                            }}
                                            aria-label="Search customers"
                                        />
                                        <span className="kt-input-icon__icon kt-input-icon__icon--right">
                                            <span><i className="la la-search" aria-hidden="true" /></span>
                                        </span>
                                    </div>
                                </form>
                                {showBulkDelete && (
                                    <div className="customers-info-toolbar-actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm bulk-delete-button"
                                            disabled={selectedContactIds.length === 0 || !bulkDeleteEnabled}
                                            title="Delete"
                                            aria-label="Delete"
                                            onClick={() => {
                                                if (isCampaignCompleted(campaign?.status)) return;
                                                if (!bulkDeleteEnabled) return;
                                                if (selectedContactIds.length === 0) return;
                                                setShowBulkConfirmModal(true);
                                            }}
                                        >
                                            <i className="fa fa-trash" style={{ fontSize: '25px', margin: '-5px 0px 0px 3px' }} aria-hidden="true" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div
                                className="table-responsive campaign-listing-table"
                                style={{
                                    overflowX: 'auto',
                                    overflowY: 'visible',
                                }}
                            >
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            {showBulkDelete && (
                                                <th
                                                    style={{
                                                        verticalAlign: 'middle',
                                                        textAlign: 'center',
                                                        width: '40px',
                                                    }}
                                                >
                                                    <input
                                                        ref={selectAllCheckboxRef}
                                                        type="checkbox"
                                                        checked={allBulkRowsSelectedOnPage}
                                                        onChange={toggleSelectAllBulkCheckboxes}
                                                        disabled={
                                                            bulkSelectableContacts.length === 0 ||
                                                            !bulkDeleteEnabled
                                                        }
                                                        title={
                                                            !bulkDeleteEnabled &&
                                                            isCampaignCompleted(campaign?.status)
                                                                ? 'Completed campaigns cannot delete contacts'
                                                                : 'Select all deletable contacts on this page'
                                                        }
                                                        aria-label="Select all deletable contacts on this page"
                                                    />
                                                </th>
                                            )}
                                            <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '20%', fontSize: 14, fontWeight: 700 }}>Name</th>
                                            <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '20%', fontSize: 14, fontWeight: 700 }}>Phone</th>
                                            <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%', fontSize: 14, fontWeight: 700 }}>Date Added</th>
                                            <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%', fontSize: 14, fontWeight: 700 }}>Status</th>
                                            <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%', fontSize: 14, fontWeight: 700 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentCustomers.length > 0 ? (
                                            currentCustomers.map((customer, idx) => (
                                                <tr key={customer.id || idx}>
                                                    {showBulkDelete && (
                                                        <td
                                                            style={{
                                                                verticalAlign: 'middle',
                                                                textAlign: 'center',
                                                                width: '40px',
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedContactIds.includes(customer.id)}
                                                                onChange={() => toggleBulkCheckbox(customer)}
                                                                disabled={
                                                                    !contactHasServerId(customer) ||
                                                                    !bulkDeleteEnabled
                                                                }
                                                                title={
                                                                    !bulkDeleteEnabled &&
                                                                    isCampaignCompleted(campaign?.status)
                                                                        ? 'Completed campaigns cannot delete contacts'
                                                                        : !customer?.id
                                                                          ? 'Cannot select this row'
                                                                          : 'Select for bulk delete'
                                                                }
                                                                aria-label={`Select ${customer.name || 'contact'} for bulk delete`}
                                                            />
                                                        </td>
                                                    )}
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <strong>{customer.name}</strong>
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'left', fontFamily: 'monospace' }}>
                                                        {(() => {
                                                            const digits = String(customer.phone_number || '').replace(/\D/g, '');
                                                            const display = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                                                            return display ? `+1${display}` : customer.phone_number;
                                                        })()}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                        {formatDate(customer.created_at)}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <span className={executionStatusBadgeClass(customer.execution_status)}>
                                                            {customer.execution_status || 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <span style={{ display: 'inline-flex', gap: '8px', justifyContent: 'flex-start' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0"
                                                                onClick={() => handleViewCustomer(customer)}
                                                                title="View customer details"
                                                            >
                                                                <i className="fa fa-eye" aria-hidden="true" />
                                                            </button>
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={customersTableColSpan} className="text-start text-muted py-4">
                                                    No record found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-3 pt-2 campaign-customers-footer">
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: 25 }}>
                                    <small className="text-muted d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                                        <span className="mr-1">Show</span>
                                        <DatatablePageSizeSelect
                                            limit={String(customersPerPage)}
                                            onChangeLimit={(e, { value }) => {
                                                setCustomersPerPage(Number(value) || 10);
                                                setCurrentPage(1);
                                            }}
                                        />
                                        <span className="ml-1">entries</span>
                                    </small>
                                    <small className="text-muted">
                                        {totalCustomers === 0
                                            ? 'Showing 0 of 0'
                                            : `Showing ${startIndex + 1}-${Math.min(endIndex, totalCustomers)} of ${totalCustomers}`}
                                    </small>
                                </div>
                                <Pagination
                                    size="mini"
                                    activePage={activePage}
                                    totalPages={totalPages}
                                    onPageChange={(e, { activePage }) => {
                                        if (totalCustomers > 0) {
                                            handlePageChange(e, { activePage });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Details Modal */}
            <Modal
                show={showCustomerDetailsModal}
                maxWidth="820px"
                title="Customer Details"
                headerClassName="linear-gradient"
                onclose={() => {
                    setShowCustomerDetailsModal(false);
                    setSelectedCustomer(null);
                }}
                body={
                    <div style={{ padding: '12px 20px 20px', textAlign: 'left', minWidth: 0 }}>
                        <CustomerDetailsModalBody
                            selectedCustomer={selectedCustomer}
                            phoneDisplay={
                                selectedCustomer?.phone_number
                                    ? `+1${phoneFormat(selectedCustomer.phone_number)}`
                                    : 'N/A'
                            }
                            showEmptyDynamicVariablesMessage
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2 ov-cd-modal-footer">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCustomerDetailsModal(false);
                                setSelectedCustomer(null);
                            }}
                            className="btn linear-gradient yr-submitbtn btn-sm"
                        >
                            Close
                        </button>
                    </div>
                }
            />

            {/* CSV Upload Modal */}
            <Modal
                show={showCSVModal}
                maxWidth="600px"
                title="Upload Customers"
                onclose={() => {
                    setShowCSVModal(false);
                    setCsvFile(null);
                    setCsvError("");
                    setCsvSuccess("");
                    if (fileInputRef.current) {
                        fileInputRef.current.value = null;
                    }
                }}
                body={
                    <div className="compaign-form-detail">
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 12, lineHeight: 1.4 }}>
                            Upload a CSV file with columns: <strong>Name</strong>, <strong>Phone</strong>. 
                            Additional columns will be treated as dynamic variables.
                        </div>
                        {csvSuccess && (
                            <Message
                                color="green"
                                style={{ display: csvSuccess ? "block" : "none", marginTop: 30 }}
                            >
                                {csvSuccess}
                            </Message>
                        )}
                        {csvError && (
                            <div style={{ color: 'red', marginBottom: '10px' }}>
                                {Array.isArray(csvError) ? (
                                    <div>
                                        <ul style={{ margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                            {csvError.map((err, i) => (
                                                <li key={i} style={{ marginBottom: '4px' }}>
                                                    {err}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <span>{csvError}</span>
                                )}
                            </div>
                        )}
                        {csvProcessing && <div className="ui loading form"></div>}
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    setCsvFile(e.target.files[0]);
                                    setCsvError("");
                                    setCsvSuccess("");
                                }
                            }}
                            className="form-control my-3"
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCSVModal(false);
                                clearCSVInputAndErrors();
                            }}
                            className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        >
                            <span>Cancel</span>
                        </button>

                        <button
                            type="button"
                            onClick={handleCSVUpload}
                            disabled={loading || csvProcessing || csvError || csvSuccess}
                            className="btn linear-gradient yr-submitbtn btn-sm"
                            style={{
                                opacity: csvProcessing ? 0.6 : 1,
                                cursor: csvProcessing ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                minWidth: '90px',
                            }}
                        >
                             {csvProcessing ? "Processing..." : "Save"}
                        </button>
                    </div>
                }
            />

            {/* Webhook Mapping Guide Modal - Only for triggered campaigns */}
            {isTriggered && (
                <Modal
                    show={showWebhookInfoModal}
                    maxWidth="820px"
                    title="Webhook Mapping Guide"
                    onclose={() => setShowWebhookInfoModal(false)}
                    body={
                        <div style={{ padding: '10px' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: '#333' }}>Overview</h4>
                                <p style={{ color: '#666', lineHeight: '1.6' }}>
                                    When sending data to this webhook URL, you need to include the following fields in your POST request payload.
                                </p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: '#333' }}>Required Fields</h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field Name</th>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>name</td>
                                                <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                <td style={{ padding: '10px', color: '#666' }}>
                                                    Customer's full name. Must be 2-50 characters.
                                                </td>
                                            </tr>
                                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>phone_number</td>
                                                <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                <td style={{ padding: '10px', color: '#666' }}>
                                                    Customer's phone number. Must be a valid US number containing <strong>10 digits</strong>,
                                                    with optional <strong>+1</strong>.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: '#333' }}>Optional Fields</h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field Name</th>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>dynamic_variables</td>
                                                <td style={{ padding: '10px', color: '#666' }}>object</td>
                                                <td style={{ padding: '10px', color: '#666' }}>
                                                    Extra customer data as key/value pairs.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: '#333' }}>Request Example</h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong style={{ color: '#495057' }}>HTTP Method:</strong>{' '}
                                        <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>POST</code>
                                    </div>
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong style={{ color: '#495057' }}>Content-Type:</strong>{' '}
                                        <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>application/json</code>
                                    </div>
                                    <div style={{ marginTop: '15px' }}>
                                        <strong style={{ color: '#495057', display: 'block', marginBottom: '8px' }}>Request Body:</strong>
                                        <pre style={{
                                            backgroundColor: '#2d2d2d',
                                            color: '#f8f8f2',
                                            padding: '15px',
                                            borderRadius: '5px',
                                            overflow: 'auto',
                                            fontSize: '13px',
                                            lineHeight: '1.5',
                                            margin: 0
                                        }}>
{`{
  "customers": [
    {
      "name": "John Doe",
      "phone_number": "8258044811",
      "dynamic_variables": {
        "CompanyName": "Acme Inc",
        "AppointmentDate": "Tomorrow"
      }
    },
    {
      "name": "Jane Smith",
      "phone_number": "5551234567",
      "dynamic_variables": {
        "CompanyName": "Widget Co",
        "AppointmentDate": "Day after tomorrow"
      }
    }
  ]
}`}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    footer={
                        <div className="modal-footer d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowWebhookInfoModal(false)}
                                className="btn linear-gradient yr-submitbtn btn-sm"
                            >
                                Close
                            </button>
                        </div>
                    }
                />
            )}

            <ConfirmationModal
                open={showBulkConfirmModal}
                onClose={() => setShowBulkConfirmModal(false)}
                onConfirm={confirmBulkDelete}
                title="Delete selected contacts"
                content={`You are about to permanently delete ${selectedContactIds.length} contact(s) from this campaign. This cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel="Delete"
            />
        </div>
    );
};

export default SMSCampaignDetails;